import { TableHeaderColumn, TableProps } from "@kanda-libs/ks-component-ts";
import { StringIndexedObject } from "@kanda-libs/ks-design-library";
import type { Lead } from "@kanda-libs/ks-frontend-services";
import { URLS } from "components/shared/App/constants";
import usePagination from "hooks/usePagination";
import useSearchQuery from "hooks/useSearchQuery";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { selectors } from "store";
import { SEARCH_OPTIONS, TABLE_COLUMNS } from "./constants";
import { sortData } from "./helpers";
import useLeadTableSorting from "./useLeadTableSorting";

export type LeadTablePropsHook = TableProps;

export default function useLeadTableProps(leads: Lead[]): LeadTablePropsHook {
  const { push } = useHistory();

  const { sorting, order, onAction } = useLeadTableSorting();

  const { hits, query } = useSearchQuery(leads, "jobs", SEARCH_OPTIONS);
  const isLoading = useSelector(selectors.getIsLoading);

  const { pageIndex, perPage, totalPages, setPageIndex } = usePagination(
    hits.length
  );

  // Data slicing is used in place of API pagination - in real use, use API
  // pagination
  const paginatedData = useMemo(() => {
    const rawData = (hits || []).map(({ item }) => item);

    return sortData(rawData, sorting, order).slice(
      pageIndex * perPage,
      pageIndex * perPage + perPage
    );
  }, [hits, pageIndex, perPage, sorting]);

  const setPage = useCallback(
    (index: number) => setPageIndex(index),
    [setPageIndex]
  );

  const queryRef = useRef("");

  // Effect resets page index to 0 when query changes - when user searches
  useEffect(() => {
    if (queryRef.current && queryRef.current === query) return;
    setPageIndex(0);
  }, [query, setPageIndex]);

  const onRowClicked = useCallback(
    (row?: StringIndexedObject) => {
      if (row) {
        push(URLS.lead.replace(":id", row.original.id));
      }
    },
    [push]
  );

  return {
    data: paginatedData,
    onRowClicked,
    pageIndex,
    totalPages,
    setPage,
    columns: TABLE_COLUMNS as unknown as TableHeaderColumn[],
    isLoading,
    onAction,
  };
}
